import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Voice-note client helpers for the coach dashboard. Two flows:
 *
 *   1. Record + upload  — coach records a voice note in the browser
 *      via MediaRecorder, blob lands in the `coach-voice-notes`
 *      bucket at users/<auth.uid()>/<uuid>.m4a. The returned path
 *      is written to `coach_annotations.audio_path`.
 *
 *   2. Sign + play     — consumer (coach viewing a student's voice
 *      reply, or vice versa) POSTs the path to the
 *      `coach-voice-note-sign` edge function and plays the returned
 *      signed URL in an <audio> element.
 *
 * Storage bucket + RLS come from the companion migration.
 */

export const COACH_VOICE_NOTE_BUCKET = "coach-voice-notes";

export interface UploadedVoiceNote {
  audioPath: string;
  durationSeconds: number;
  sizeBytes: number;
  mimeType: string;
}

/**
 * Check whether the browser supports recording. Safari and Firefox
 * ship MediaRecorder but historically default to different mime types;
 * the caller decides whether to hide the recorder or fall back to
 * file-upload when this returns false.
 */
export function voiceRecordingSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices !== "undefined" &&
    typeof MediaRecorder !== "undefined"
  );
}

/**
 * Pick a mime type the browser will actually record. Browsers vary:
 * Chrome prefers webm, Safari prefers mp4. We probe a short list and
 * fall back to whatever MediaRecorder defaults to (empty string).
 */
export function preferredVoiceRecordingMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return "";
}

/**
 * Upload a recorded blob into the voice-notes bucket. Returns the
 * storage path to persist on the owning row. Path shape:
 *   users/<auth.uid()>/<uuid>.<ext>
 */
export async function uploadVoiceNote(
  client: SupabaseClient,
  params: {
    userId: string;
    blob: Blob;
    durationSeconds: number;
  },
): Promise<UploadedVoiceNote> {
  const mimeType = params.blob.type || "audio/webm";
  const ext = extensionForMime(mimeType);
  const filename = `${crypto.randomUUID()}.${ext}`;
  const audioPath = `users/${params.userId}/${filename}`;

  const { error } = await client.storage
    .from(COACH_VOICE_NOTE_BUCKET)
    .upload(audioPath, params.blob, {
      contentType: mimeType,
      cacheControl: "3600",
      upsert: false,
    });
  if (error) throw new Error(`voice upload failed: ${error.message}`);

  return {
    audioPath,
    durationSeconds: params.durationSeconds,
    sizeBytes: params.blob.size,
    mimeType,
  };
}

/**
 * Mint a short-lived signed URL for a previously-uploaded voice
 * note. Delegates to the `coach-voice-note-sign` edge function so
 * membership is verified server-side. Cache the URL for the window
 * it's valid — we don't deduplicate here.
 */
export async function signVoiceNoteUrl(
  client: SupabaseClient,
  audioPath: string,
): Promise<{ url: string; expiresIn: number }> {
  const { data, error } = await client.functions.invoke<{
    ok: boolean;
    url?: string;
    expires_in?: number;
    error?: string;
  }>("coach-voice-note-sign", {
    body: { audio_path: audioPath },
  });
  if (error) throw new Error(error.message);
  if (!data?.ok || !data.url) {
    throw new Error(data?.error ?? "signed URL unavailable");
  }
  return { url: data.url, expiresIn: data.expires_in ?? 300 };
}

function extensionForMime(mime: string): string {
  if (mime.startsWith("audio/webm")) return "webm";
  if (mime.startsWith("audio/mp4")) return "m4a";
  if (mime.startsWith("audio/ogg")) return "ogg";
  if (mime.startsWith("audio/wav")) return "wav";
  if (mime.startsWith("audio/mpeg")) return "mp3";
  return "bin";
}
